/**
 * Calendar Event Card Component
 * Displays event summary in list views
 */

import { Link } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Stack,
  Avatar,
  AvatarGroup,
  Skeleton,
} from '@mui/material'
import {
  CalendarMonth,
  LocationOn,
  People,
  Event as EventIcon,
} from '@mui/icons-material'
import {
  CalendarEventDto,
  CalendarEventStatus,
  getEventStatusLabel,
  getEventStatusColor,
  getRsvpStatusLabel,
  getRsvpStatusColor,
} from '@api/types/calendarEvent'
import { isEventPast } from '../types/calendar-event-editor.types'

interface CalendarEventCardProps {
  event: CalendarEventDto
  variant?: 'default' | 'compact'
  /** Preview attendees to display avatars (if available) */
  attendeeAvatars?: Array<{ userId: string; profileImageUrl?: string; displayName: string }>
}

/**
 * Format date for display
 */
function formatEventDate(dateString: string, isAllDay: boolean): string {
  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }
  if (!isAllDay) {
    options.hour = 'numeric'
    options.minute = '2-digit'
  }
  return date.toLocaleString(undefined, options)
}

/**
 * Format date range for display
 */
function formatDateRange(start: string, end: string, isAllDay: boolean): string {
  const startDate = new Date(start)
  const endDate = new Date(end)

  // Same day
  if (startDate.toDateString() === endDate.toDateString()) {
    if (isAllDay) {
      return formatEventDate(start, true)
    }
    const startTime = startDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
    const endTime = endDate.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
    return `${formatEventDate(start, true)} ${startTime} - ${endTime}`
  }

  // Different days
  return `${formatEventDate(start, isAllDay)} - ${formatEventDate(end, isAllDay)}`
}

export function CalendarEventCard({ event, variant = 'default', attendeeAvatars }: CalendarEventCardProps) {
  const isPast = isEventPast(event.endDateTime)
  const isDraft = event.status === CalendarEventStatus.Draft
  const isCancelled = event.status === CalendarEventStatus.Cancelled

  return (
    <Card
      component={Link}
      to={`/events/${event.id}`}
      sx={{
        display: 'flex',
        flexDirection: variant === 'compact' ? 'row' : 'column',
        textDecoration: 'none',
        color: 'inherit',
        opacity: isPast || isCancelled ? 0.7 : 1,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px) scale(1.01)',
          '& .MuiCardMedia-root': {
            transform: 'scale(1.05)',
          },
        },
        '& .MuiCardMedia-root': {
          transition: 'transform 0.3s ease',
        },
      }}
    >
      {/* Cover Image */}
      {event.coverImageUrl ? (
        <CardMedia
          component="img"
          sx={{
            width: variant === 'compact' ? 120 : '100%',
            height: variant === 'compact' ? 120 : 160,
            objectFit: 'cover',
          }}
          image={event.coverImageUrl}
          alt={event.title}
        />
      ) : (
        <Box
          sx={{
            width: variant === 'compact' ? 120 : '100%',
            height: variant === 'compact' ? 120 : 160,
            backgroundColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EventIcon sx={{ fontSize: 48, color: 'grey.400' }} />
        </Box>
      )}

      <CardContent sx={{ flex: 1, p: 2 }}>
        {/* Status Badges */}
        <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
          {isDraft && (
            <Chip
              label={getEventStatusLabel(event.status)}
              color={getEventStatusColor(event.status)}
              size="small"
            />
          )}
          {isCancelled && (
            <Chip
              label={getEventStatusLabel(event.status)}
              color={getEventStatusColor(event.status)}
              size="small"
            />
          )}
          {isPast && !isCancelled && (
            <Chip label="Past" color="default" size="small" />
          )}
          {event.isEventFull && !isPast && !isCancelled && (
            <Chip label="Event Full" color="warning" size="small" />
          )}
          {event.userRsvp && (
            <Chip
              label={getRsvpStatusLabel(event.userRsvp.status)}
              color={getRsvpStatusColor(event.userRsvp.status)}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Title */}
        <Typography
          variant="h6"
          component="h3"
          gutterBottom
          sx={{
            textDecoration: isCancelled ? 'line-through' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {event.title}
        </Typography>

        {/* Quick Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {event.quickDescription}
        </Typography>

        {/* Date/Time */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <CalendarMonth fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {formatDateRange(event.startDateTime, event.endDateTime, event.isAllDay)}
          </Typography>
        </Stack>

        {/* Location */}
        {event.location?.name && (
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <LocationOn fontSize="small" color="action" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {event.location.name}
            </Typography>
          </Stack>
        )}

        {/* Attendance with avatar stack */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          {attendeeAvatars && attendeeAvatars.length > 0 ? (
            <>
              <AvatarGroup
                max={4}
                sx={{
                  '& .MuiAvatar-root': {
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    border: '2px solid',
                    borderColor: 'background.paper',
                  },
                }}
              >
                {attendeeAvatars.map((attendee) => (
                  <Avatar
                    key={attendee.userId}
                    src={attendee.profileImageUrl}
                    alt={attendee.displayName}
                  >
                    {attendee.displayName?.charAt(0) || '?'}
                  </Avatar>
                ))}
              </AvatarGroup>
              {(event.attendanceStats?.attendingCount ?? 0) > 4 && (
                <Typography variant="caption" color="text.secondary">
                  +{(event.attendanceStats?.attendingCount ?? 0) - 4} more
                </Typography>
              )}
            </>
          ) : (
            <>
              <People fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {event.attendanceStats?.attendingCount ?? 0}
                {event.maxAttendees ? `/${event.maxAttendees}` : ''} attending
              </Typography>
            </>
          )}
        </Stack>

        {/* Owner */}
        {event.owner && (
          <Stack direction="row" spacing={1} alignItems="center" mt="auto">
            <Avatar
              src={event.owner.entityAvatarUrl}
              sx={{ width: 24, height: 24 }}
            >
              {event.owner.entityName?.charAt(0) || '?'}
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {event.owner.entityName}
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Loading skeleton for event card
 */
export function CalendarEventCardSkeleton({ variant = 'default' }: { variant?: 'default' | 'compact' }) {
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: variant === 'compact' ? 'row' : 'column',
      }}
    >
      <Skeleton
        variant="rectangular"
        sx={{
          width: variant === 'compact' ? 120 : '100%',
          height: variant === 'compact' ? 120 : 160,
        }}
      />
      <CardContent sx={{ flex: 1, p: 2 }}>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="80%" height={28} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1.5 }} />
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width="30%" height={16} />
        </Stack>
      </CardContent>
    </Card>
  )
}

export default CalendarEventCard
